<template>
    <div class="report-editor">

        <div class="row">
            <div class="col">
                <h2>
                    {{ $t('edit-report-config') }}
                    <button @click="save" class="btn btn-primary float-right">{{ $t('save') }}</button>
                </h2>
                <div class="alert alert-success mt-8" v-if="saved">{{ $t('saved') }}</div>
            </div>
        </div>

        <div class="row">
            <div class="col-6">
                <div class="form-group">
                    <label>{{ $t('name') }}</label>
                    <input class="form-control" v-model="reportconfig.reportconfig_name" />
                </div>
            </div>
            <div class="col-6">
                <div class="form-group">
                    <label>{{ $t('title') }}</label>
                    <input class="form-control" v-model="reportconfig.reportconfig_title" />
                </div>
            </div>
            <div class="col-6">
                <div class="form-group">
                    <label>{{ $t('type') }}</label>
                    <select class="form-control" v-model="reportconfig.reportconfig_teacher">
                    	<option :value="false">{{ $t('student') }}</option>
                    	<option :value="true">{{ $t('teacher') }}</option>
                    </select>
                </div>
            </div>
        </div>


        <div class="row">
            <div class="col-4">
                <div class="form-group">
                    <label>Programme</label>
                    <select class="form-control" v-model="reportconfig.programme_id">
                        <option :value="null">Any</option>
                        <option
                        v-for="programme in programmes"
                        :key="programme.Id"
                        :value="programme.Id"
                        >{{ programme.Name}}
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-4">
                <div class="form-group">
                    <label>Year</label>
                    <select class="form-control" v-model="reportconfig.year">
                        <option :value="null">Any</option>
                        <template v-if="selectedProgramme">
                            <option
                            v-for="year in parseInt(selectedProgramme.CourseLengthYears)"
                            :key="year"
                            :value="year"
                            >{{ year }}
                            </option>
                            <option :value="parseInt(selectedProgramme.CourseLengthYears) + 1">{{ $t('graduated') }}</option>
                        </template>
                    </select>
                </div>
            </div>
            <div class="col-4">
                <div class="form-group">
                    <label>{{ $t("session-type") }}</label>
                    <SessionTypeMultiSelect
                    	:selecteditems="session_type"
                    />
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-4">
                <div class="form-group">
                    <label>{{ $t("grades") }}</label>
                    <GradesMultiSelect
                    	:grades="grades"
                    />
                </div>
            </div>

            <div class="col-4" v-for="grade in this.grades" :key="grade.id">
            	<div class="form-group">
                    <label>{{ grade.caption }}</label>
                    <GradeValueMultiSelect
                    	:grade="grade"
                    />
            	</div>
            </div>
		</div>

        <draggable :list="grid" group="reportrows" class="grid" handle=".handle">
            <div class="row-wrap" v-for="(row, r) in grid" :key="r">
				<div class="row-heading">
					<span class="handle"><b-icon icon="arrows-move" width="1em" height="1em"></b-icon></span>
                	<button class="btn btn-sm shadow btn-success add-col" @click="addCol(row)">{{ $t('add-column') }}</button>
				</div>
                <draggable :list="row" group="reportcols" tag="div" class="row" handle=".handle">
                    <div v-for="(col, c) in row" :key="c" class="col" :class="`col-${col.width}`">
                        <div>
                            <div class="form-group">
                                <!-- data string -->
                                <label><span class="handle"><b-icon icon="arrows-move" width="1em" height="1em"></b-icon></span> {{ $t('width') }}</label>
                                <input type="number" class="form-control" v-model="col.width" />
                            </div>
                            <draggable :list="col.fields" group="reportelements">
								<div v-for="(field, f) in col.fields" class="field" :key="f"  @click="editField(field)">
									<a href="#" @click.prevent.stop="deleteField(col, f)" class="delete" :title="$t('delete-field')">
										<b-icon icon="trash" width="1em" height="1em"></b-icon>
									</a>
									<h3>{{ field.caption }}</h3>
									<p><strong>{{ field.type }}</strong></p>
									<template v-if="fieldtypeHasMultipleDataPoints(field.type)">
										{{ $tc('n-datums', field.data.length) }}
									</template>
									<template v-else>
										{{ field.data ? field.data : $t('no-data') }}
									</template>
								</div>
      						</draggable>
                            <button class="btn btn-success btn-sm" @click="addField(col)">{{ $t('add-field') }}</button>
                        </div>
                    </div>
      			</draggable>
            </div>
        </draggable>

        <div class="row">
            <div class="col">
                <button class="btn btn-success float-right" @click="addRow">{{ $t('add-row') }}</button>
            </div>
        </div>

        <div class="reporteditor__sidebar" :class="{'d-none': !editing}" style="z-index: 4">
            <div>
                <p><strong>{{ $t('editing-field') }}</strong>
                    <a class="btn btn-light btn-xs float-right"
                       style="margin: -5px 0 0 0"
                       @click="removeEditing()"><b-icon icon="x" width="1em" height="1em"></b-icon></a>
                </p>
            </div>

            <!-- editor here -->
            <template v-if="editing">
                <div class="form-group">
                    <label>{{ $t('caption') }}</label>
                    <input class="form-control" v-model="editing.caption" />
                </div>
                <div class="form-group">
                    <label>{{ $t('type') }}</label>
                    <select v-model="editing.type" class="form-control" @change="typeChange">
                        <option :value="null">None</option>
                        <option
                        v-for="type in fieldtypes"
                        :key="type.name"
                        :value="type.name"
                        >{{ type.name }}
                        </option>
                    </select>
                </div>
                <template v-if="fieldtypeHasData(editing.type)">
                    <template v-if="fieldtypeHasMultipleDataPoints(editing.type)">
                        <!-- caption and data string for each -->
                        <fieldset v-for="(datum, d) in editing.data" :key="d" class="data">
                            <div class="form-group">
                                <label>{{ $t('caption') }}</label>
                                <input class="form-control" v-model="datum.caption" />
                            </div>
                            <div class="form-group">

                                <label class="dataelement-label">
                                    <span>{{ $t('report-data-element') }}</span>
                                    <span>
                                        <a href="#" @click.prevent="editDataelement(editingData[d].reportdataelement)" v-show="editingData[d].reportdataelement">Edit</a>
                                        <a href="#" @click.prevent="newDataelement()">New</a>
                                    </span>
                                </label>

                                <input
                                    type="text"
                                    :value="editingData[d].reportdataelement"
                                    class="form-control"
                                    @focus="selectDataelement(editing, d)"
                                />

                                <label>{{ $t('property') }}</label>
                                <input
                                    type="text"
                                    :value="editingData[d].property"
                                    class="form-control"
                                    @focus="selectDataelementProperty(editing, d)"
                                />

                                <label>{{ $t('colour') }}</label>
                                <select
                                    v-model="datum.color"
                                    class="form-control"
                                >
                                	<option value="">{{$t('auto')}}</option>
                                	<option value="green">{{$t('green')}}</option>
                                	<option value="yellow">{{$t('yellow')}}</option>
                                	<option value="amber">{{$t('amber')}}</option>
                                	<option value="red">{{$t('red')}}</option>
                                	<option value="blue">{{$t('blue')}}</option>
                                	<option value="purple">{{$t('purple')}}</option>
                                	<option value="teal">{{$t('teal')}}</option>
                                	<option value="brown">{{$t('brown')}}</option>
                                </select>

                            </div>
                            <a href="#" @click.prevent="deleteDatum(editing, d)" class="delete" :title="$t('delete-datum')">
                                <b-icon icon="trash" width="1em" height="1em"></b-icon>
                            </a>
                        </fieldset>
                        <button class="btn btn-success btn-sm" @click="addDatum(editing)">{{ $t('add-datum') }}</button>
                    </template>
                    <div v-else-if="editing.type" class="data">
                    	<div class="form-group">
							<label class="dataelement-label">
								<span>{{ $t('report-data-element') }}</span>
								<span>
									<a href="#" @click.prevent="editDataelement(editingData[0].reportdataelement)" v-show="editingData[0].reportdataelement">Edit</a>
									<a href="#" @click.prevent="newDataelement()">New</a>
								</span>
							</label>
							<input
								type="text"
								:value="editingData[0].reportdataelement"
								class="form-control"
								@focus="selectDataelement(editing, 0)"
							/>
						</div>
                    	<div class="form-group">
							<label>{{ $t('property') }}</label>
							<input
								type="text"
								:value="editingData[0].property"
								class="form-control"
								@focus="selectDataelementProperty(editing, 0)"
							/>
						</div>

                        <template v-if="editing.type == 'bargraphhorizontal'">
                        	<div class="form-group">
								<label>
									<input type="checkbox" v-model="editing.showquartiles">
									{{$t('show-quartiles')}}
								</label>
                        	</div>
                        	<div class="form-group">
								<label>
									<input type="checkbox" v-model="editing.reversed">
									{{$t('bar-reversed')}}
								</label>
                        	</div>
                        	<div class="form-group">
								<label>
									<input type="checkbox" v-model="editing.inverted">
									{{$t('colors-inverted')}}
								</label>
                        	</div>
                        	<div class="form-group">
								<label>
									<input type="checkbox" v-model="editing.neutral">
									{{$t('color-neutral')}}
								</label>
                        	</div>
                        </template>

                    </div>
                </template>
            </template>

            <div>
                <button class="btn btn-primary float-right" @click="removeEditing">{{ $t('done') }}</button>
            </div>
        </div>
    </div>
</template>

<script>

import draggable from 'vuedraggable';

import SessionTypeMultiSelect from 'src/components/Inputs/SessionTypeMultiSelect';
import GradesMultiSelect from 'src/components/Inputs/GradesMultiSelect';
import GradeValueMultiSelect from 'src/components/Inputs/GradeValueMultiSelect';

export default {
	components: {
		draggable,
		SessionTypeMultiSelect,
		GradesMultiSelect,
		GradeValueMultiSelect
	},
    data() {
        return {
            dataelements: [],
            dataelementPropertyListCache: {},
            datasourceReturnPropertyListCache: {},
            reportconfig_id: null,
            editing: null,
            editingData: [/*{
                reportdataelement: null,
                property: null
            }*/],
            fieldtypes: [
                {name:'bargraphhorizontal', hasData: true, multipleDataPoints: false},
                {name:'radialgraphclockwise', hasData: true, multipleDataPoints: false},
                {name:'piechart', hasData: true, multipleDataPoints: true},
                {name:'linegraph', hasData: true, multipleDataPoints: false},
                {name:'datepassed', hasData: true, multipleDataPoints: false},
                {name:'textlist', hasData: true, multipleDataPoints: false},
                {name:'tablehorizontal', hasData: true, multipleDataPoints: true},
                {name:'tablevertical', hasData: true, multipleDataPoints: true},
                {name:'heading', hasData: false }
            ],
            saved: false,
            session_type: [],
            grades: [],
            reportconfig: {
                reportconfig_name: null,
                reportconfig_title: null,
                reportconfig_teacher: false,
                programme_id: null,
                year: null,
                reportconfig_json: {
                    filters: {
                        // "session_type": 1
                    },
                    grid: [],
                }
            }
        }
    },
    watch: {
    },
    methods: {

        /* ---------- Rows ---------- */
        getDefaultRow() {
            return [this.getDefaultCol()];
        },

        addRow() {
            this.reportconfig.reportconfig_json.grid.push(this.getDefaultRow());
        },
        /* ---------- ----------  ---------- */




        /* ---------- Cols ---------- */
        getDefaultCol() {
            return {
                fields: [this.getDefaultField()],
                width: 6
            }
        },

        addCol(row) {
            row.push(this.getDefaultCol());
        },
        /* ---------- ----------  ---------- */




        /* ---------- Fields ---------- */
        getDefaultField() {
            return {
                caption: 'New field',
                type: null,
                neutral: false ,
                inverted: false ,
                reversed: false ,
                showquartiles: false
            }
        },

        addField(col) {
            col.fields.push(this.getDefaultField())
        },

        deleteField(col, i) {
            this.$delete(col.fields, i);
            this.removeEmptyGridItems();
        },

        fieldtypeHasData(name) {
            const fieldtype = this.getFieldtypeByName(name);
            return fieldtype && fieldtype.hasData;
        },

        fieldtypeHasMultipleDataPoints(name) {
            const fieldtype = this.getFieldtypeByName(name);
            return fieldtype && fieldtype.multipleDataPoints;
        },

        getFieldtypeByName(name) {
            for(const fieldtype of this.fieldtypes) {
                if(fieldtype.name == name) {
                    return fieldtype;
                }
            }
            return false;
        },

        editField(field) {
            this.editingData = [];
            const data = Array.isArray(field.data) ? field.data : [{caption:null,data:field.data}];
            for(const datum of data) {
                this.editingData.push(this.getDataObjFromStr(datum.data));
            }
            this.fetchDataelementPropertyListsForSelectedDataelements();
            this.editing = field;
        },
        /* ---------- ----------  ---------- */




        /* ---------- Data ---------- */
        getDefaultDatum() {
            return {
                caption: 'New datum',
                data: null
            }
        },

        addDatum(field) {
            field.data.push(this.getDefaultDatum());
            this.editingData.push(this.getDefaultDataObj());
        },

        deleteDatum(field, i) {
            this.$delete(field.data, i);
            this.$delete(this.editingData, i);
        },

        updateDatum(field, i) {
            // call on reportdataelement/property select
            // update the string for this datum

            const multi = this.fieldtypeHasMultipleDataPoints(field.type);

            const oldStr = multi ? field.data[i].data : field.data;
            const oldObj = this.getDataObjFromStr(oldStr);

            if(this.editingData[i].reportdataelement != oldObj.reportdataelement) {
                this.editingData[i].property = null;
                this.fetchDataelementPropertyListsForSelectedDataelements();
            }

            const newStr = `${this.editingData[i].reportdataelement}.${this.editingData[i].property}`;

            if(multi) {
                field.data[i].data = newStr
            } else {
                field.data = newStr;
            }

        },

        getDefaultDataObj() {
            return {
                reportdataelement: null,
                property: null
            }
        },

        getDataObjFromStr(str) {
            let parts;
            if(str) {
                parts = str.split('.');
            }
            return {
                reportdataelement: parts ? parts[0] : null,
                property: parts ? parts[1] : null
            }
        },

        fetchDatasourceReturnPropertyList(datasourceName, count) {

            return new Promise((resolve/*, reject*/) => {
                if(Object.hasOwnProperty.call(this.datasourceReturnPropertyListCache, datasourceName)) {
                    resolve(this.datasourceReturnPropertyListCache[datasourceName]);
                } else {

                    this.datasourceReturnPropertyListCache[datasourceName] = [];

                    this.$cfetch(`${this.$settings.APIBaseURL}/reporting/alldata/datasourcereturnpropertylist/${datasourceName}` ,
                    	{
                    		post: true ,
                    		data: {
                    			count: count
                    		}
                    	}
                    )
                    .then(json => {

                        for(const propertyName of json.fieldnames) {
                            this.datasourceReturnPropertyListCache[datasourceName].push(propertyName);
                        }

                        resolve(this.datasourceReturnPropertyListCache[datasourceName]);

                    }).catch(error => {
                        console.log(error);
                        this.$log.error.call(this, error);
                    });
                }
            });
        },

        fetchDataelementPropertyList(dataelement) {

            if(Object.hasOwnProperty.call(this.dataelementPropertyListCache, dataelement.name)) {
                // already got it
                return true;
            }

            this.$set(this.dataelementPropertyListCache, dataelement.name, []);

            this.fetchDatasourceReturnPropertyList(dataelement.data.datasource , dataelement.data.count)
            .then(properties => {

                if(dataelement.data.return) {
                    for(const property of dataelement.data.return) {

                        this.dataelementPropertyListCache[dataelement.name].push(this.getDataelementPropertyNameFromValue(property));

                    }
                } else {
                    for(const propertyName of properties) {

                        this.dataelementPropertyListCache[dataelement.name].push(propertyName);

                    }
                }

            }).catch(error => {
                console.log(error);
                this.$log.error.call(this, error);
            });
        },

        getDataelementPropertyNameFromValue(value) {
            const parts = value.split('=');
            return parts[0];
        },

        getDataelementByName(name) {
            for(const dataelement of this.dataelements) {
                if(dataelement.name == name) {
                    return dataelement;
                }
            }
            return false;
        },

        fetchDataelementPropertyListByName(name) {
            const dataelement = this.getDataelementByName(name);
            if(dataelement) {
                this.fetchDataelementPropertyList(dataelement);
            }
        },

        fetchDataelementPropertyListsForSelectedDataelements() {
            for(const datum of this.editingData) {
                this.fetchDataelementPropertyListByName(datum.reportdataelement);
            }
        },

        selectDataelement(field, i) {
            this.$store.commit("openModal", {
                type: "selector",
                title: this.$t('select-report-data-element'),
                selectCallback: (selection) => {
                    this.editingData[i].reportdataelement = selection.name;
                    this.updateDatum(field, i);
                    this.$store.commit("closeModal");

                },
                selected: [{name:this.editingData[i].reportdataelement}],
                data: this.dataelements,
                captionKey: "caption",
                idProperty: 'name'
            });
        },

        selectDataelementProperty(field, i) {

            this.$store.commit("openModal", {
                type: "selector",
                title: this.$t('select-property'),
                selectCallback: (selection) => {
                    this.editingData[i].property = selection;
                    this.updateDatum(field, i);
                    this.$store.commit("closeModal");

                },
                selected: [this.editingData[i].property],
                data: this.dataelementPropertyListCache[this.editingData[i].reportdataelement]
            });
        },

        editDataelement(reportdataelement) {
            const dataelement = this.getDataelementByName(reportdataelement);
            if(dataelement) {
                this.openDataelementEditor(dataelement.id);
            }
        },

        newDataelement() {
            this.openDataelementEditor();
        },

        openDataelementEditor(id = null) {

            let url = `/settings/reports/dataelements/edit`;
            if(id) url = `${url}/${id}`;

            //this.iframesrc = url;
            this.dataelementeditor = window.open(url);

        },
        /* ---------- ----------  ---------- */





        typeChange() {
            if(this.fieldtypeHasMultipleDataPoints(this.editing.type)) {
                if(!Array.isArray(this.editing.data)) {
                    this.$set(this.editing, 'data', []);
                }
            } else {
                if(Array.isArray(this.editing.data)) {
                    this.$set(this.editing, 'data', null);
                }
            }
        },

        removeEditing() {
            this.editing = null;
        },

        removeEmptyGridItems() {
            for(const [r, row] of this.reportconfig.reportconfig_json.grid.entries()) {
                for(const [c, col] of row.entries()) {
                    if(!col.fields.length) {
                        this.$delete(row, c);
                    }
                }
                if(!row.length) {
                    this.$delete(this.reportconfig.reportconfig_json.grid, r);
                }
            }
        },

        save() {

            if(this.session_type) {
                this.reportconfig.reportconfig_json.filters = this.reportconfig.reportconfig_json.filters ? this.reportconfig.reportconfig_json.filters : {};
                this.reportconfig.reportconfig_json.filters.session_type = this.session_type;
            }

            if(this.grades) {
                this.reportconfig.reportconfig_json.filters = this.reportconfig.reportconfig_json.filters ? this.reportconfig.reportconfig_json.filters : {};
                this.reportconfig.reportconfig_json.filters.grades = this.grades;
            }

            const data = {
                json: true,
                save: 1,
                reportconfig: this.reportconfig
            };
            if(this.reportconfig_id) {
                data.reportconfig_id = this.reportconfig_id;
            }

            this.$store.commit("showBlockingSpinner");

            this.$cfetch(`${this.$settings.APIBaseURL}/reporting/config/save`, {
                method: 'post',
                data
            })
            .then(json => {
                this.$store.commit("hideBlockingSpinner");
                if (json.status == 'SUCCESS') {
                    this.reportconfig_id = json.reportconfig.reportconfig_id;
                    this.saved = true;
                    setTimeout(() => {
                        this.saved = false;
                    }, 5000);
                }
            }).catch(error => {
                this.$store.commit("hideBlockingSpinner");
                this.$log.error.call(this, error);
            });

        },

        getReportConfig(id) {
            this.$cfetch(`${this.$settings.APIBaseURL}/reporting/config/load/${id}`)
            .then(json => {
                this.$store.commit("hideBlockingSpinner");
                if (json.status == 'SUCCESS') {

                    this.reportconfig = json.reportconfig;
                    this.reportconfig_id = json.reportconfig.reportconfig_id;

                    if(this.reportconfig.reportconfig_json.filters) {
                        const filters = this.reportconfig.reportconfig_json.filters;
                        if(!Array.isArray(filters.session_type)){
                        	filters.session_type = [filters.session_type];
                        }
                        if(filters.session_type) {
                            this.session_type = filters.session_type;
                        } else {
                        	this.session_type = [];
                        }
                        if(filters.grades) {
                            this.grades = filters.grades;
                        } else {
                        	this.grades = [];
                        }
                    }

                }
            }).catch(error => {
                this.$store.commit("hideBlockingSpinner");
                this.$log.error.call(this, error);
            });
        },

        getReportDataElements() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/reporting/dataelements/list`)
            .then(json => {
                if (json.status == 'SUCCESS') {
                    this.dataelements = json.dataelements;
                }
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },

        setupDataelementEditorDoneEventListener() {
            var eventMethod = window.addEventListener ? "addEventListener" : "attachEvent";
            var eventer = window[eventMethod];
            var messageEvent = eventMethod === "attachEvent" ? "onmessage" : "message";

            eventer(messageEvent, (e) => {

                const expectedEventName = 'cafs.reportdataelementeditor.done';

                if (e.data === expectedEventName || e.message === expectedEventName) {

                    this.dataelementeditor.close();

                    this.dataelementPropertyListCache = {};
                    this.datasourceReturnPropertyListCache = {};

                    this.getReportDataElements().then(() => {
                        this.fetchDataelementPropertyListsForSelectedDataelements();
                    });


                }

            });
        }


    },
    computed: {
        grid() {
            return this.reportconfig.reportconfig_json.grid;
        },
        selectedProgramme() {
            for(const programme of this.programmes) {
                if(programme.Id == this.reportconfig.programme_id) {
                    return programme;
                }
            }
            return null;
        },
        programmes() {
            return Object.values(this.$config.getValue('programmes'));
        }
    },
    mounted() {
        if(this.$route.params.reportconfigid) {
            this.getReportConfig(this.$route.params.reportconfigid)
        } else {
            this.$store.commit("hideBlockingSpinner");
        }
        this.getReportDataElements();
        this.setupDataelementEditorDoneEventListener();
    }
}
</script>
